import React, { useEffect, useRef, useState } from 'react';
import { formatMessage } from '../../utils/messageFormatter';

const MessageItem = ({ sender, text, timestamp, images, messageId, sessionId, feedback, onFeedback, gradeLevel }) => {
  const contentRef = useRef(null);
  const [expandedImage, setExpandedImage] = useState(null);
  const isAI = sender === 'ai';
  const isElementary = gradeLevel === 'elementary';

  useEffect(() => {
    if (isAI && contentRef.current) {
      formatMessage(contentRef.current);
    }
  }, [text, isAI]);

  const handleFeedback = (value) => {
    if (!onFeedback || !sessionId || !messageId) return;
    const newValue = feedback === value ? null : value;
    onFeedback(sessionId, messageId, newValue);
  };

  const canShowFeedback = isAI && messageId && typeof messageId === 'number';

  return (
    <div className={`message ${isAI ? 'ai-message' : 'user-message'} flex flex-col mb-4`}>
      {/* Sender label */}
      <span className={`text-xs font-medium mb-1 ${isAI ? 'text-indigo-600 self-start' : 'text-gray-500 self-end'}`}>
        {isAI ? 'Brillabee' : 'You'}
      </span>

      <div className={`flex items-end gap-2 ${isAI ? '' : 'flex-row-reverse'}`}>
        {/* AI Avatar - Brillabee logo with fallback */}
        {isAI && (
          <div className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mb-1 overflow-hidden">
            <img
              src="/assets/images/brillabee logo.png"
              alt="B"
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.textContent = 'B'; }}
            />
          </div>
        )}

        <div
          className={`message-bubble py-2 sm:py-3 px-3 sm:px-4 rounded-2xl ${
            isAI
              ? 'bg-[#f0f7ff] text-gray-800 max-w-[85%] sm:max-w-[78%] shadow-sm'
              : 'bg-indigo-50 text-gray-800 max-w-[85%] sm:max-w-[78%]'
          }`}
        >
          {/* Display images if present */}
          {images && images.length > 0 && (
            <div className="flex gap-2 mb-2 flex-wrap">
              {images.map((img, idx) => (
                <div key={idx} className="relative">
                  <img
                    src={`data:${img.mime_type};base64,${img.base64}`}
                    alt={img.filename || `Image ${idx + 1}`}
                    className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-lg cursor-pointer border-2 border-gray-200 hover:border-indigo-400 transition-all hover:scale-105"
                    onClick={() => setExpandedImage(img)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setExpandedImage(img);
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Display text message */}
          {text && text !== "[Images]" && (
            isAI ? (
              <div className="ai-message-content" ref={contentRef}>
                {text}
              </div>
            ) : (
              text
            )
          )}

          {/* Timestamp inside bubble */}
          <div className={`mt-1 text-xs text-gray-400 ${isAI ? 'text-left' : 'text-right'}`}>
            {timestamp}
          </div>
        </div>
      </div>

      {/* Feedback row */}
      {canShowFeedback && (
        <div className="flex items-center gap-1 mt-1 ml-9">
          <button
            onClick={() => handleFeedback('helpful')}
            className={`p-0.5 rounded transition-colors focus:ring-2 focus:ring-indigo-500 ${
              feedback === 'helpful'
                ? 'text-green-600'
                : 'text-gray-300 hover:text-green-500'
            }`}
            aria-label="Mark as helpful"
            title="Helpful"
          >
            <svg width={isElementary ? 18 : 14} height={isElementary ? 18 : 14} viewBox="0 0 24 24" fill={feedback === 'helpful' ? 'currentColor' : 'none'} xmlns="http://www.w3.org/2000/svg">
              <path d="M7 22V11M2 13V20C2 21.1046 2.89543 22 4 22H17.4262C18.907 22 20.1662 20.9197 20.3914 19.4562L21.4683 12.4562C21.7479 10.6389 20.3418 9 18.5032 9H15C14.4477 9 14 8.55228 14 8V4.46584C14 3.10399 12.896 2 11.5342 2C11.2093 2 10.915 2.1913 10.7831 2.48812L7.26394 10.4061C7.10344 10.7673 6.74532 11 6.35013 11H4C2.89543 11 2 11.8954 2 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {isElementary && feedback === 'helpful' && (
            <span className="text-xs text-green-600 font-medium">Helpful!</span>
          )}
          <button
            onClick={() => handleFeedback('unhelpful')}
            className={`p-0.5 rounded transition-colors focus:ring-2 focus:ring-indigo-500 ${
              feedback === 'unhelpful'
                ? 'text-red-500'
                : 'text-gray-300 hover:text-red-400'
            }`}
            aria-label="Mark as not helpful"
            title="Not helpful"
          >
            <svg width={isElementary ? 18 : 14} height={isElementary ? 18 : 14} viewBox="0 0 24 24" fill={feedback === 'unhelpful' ? 'currentColor' : 'none'} xmlns="http://www.w3.org/2000/svg">
              <path d="M17 2V13M22 11V4C22 2.89543 21.1046 2 20 2H6.57381C5.09302 2 3.83378 3.08031 3.60856 4.54379L2.53168 11.5438C2.25214 13.3611 3.65823 15 5.49683 15H9C9.55228 15 10 15.4477 10 16V19.5342C10 20.896 11.104 22 12.4658 22C12.7907 22 13.085 21.8087 13.2169 21.5119L16.7361 13.5939C16.8966 13.2327 17.2547 13 17.6499 13H20C21.1046 13 22 12.1046 22 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {isElementary && feedback === 'unhelpful' && (
            <span className="text-xs text-red-500 font-medium">Not helpful</span>
          )}
        </div>
      )}

      {/* Image modal for full-size view */}
      {expandedImage && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <button
              className="absolute -top-10 right-0 text-white text-4xl hover:opacity-80 w-10 h-10 flex items-center justify-center"
              onClick={() => setExpandedImage(null)}
              aria-label="Close image"
            >
              ×
            </button>
            <img
              src={`data:${expandedImage.mime_type};base64,${expandedImage.base64}`}
              alt={expandedImage.filename || 'Full size image'}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageItem;
